import {Field, useFormikContext} from 'formik';

import {
    FormikRadioGroup,
    FormikSelect,
    FormikTextarea,
    FormikTextInput,
    PlatformFieldset,
} from '@flexinet/ui-components';
import {Text} from '@mantine/core';
import {Application, ApplicationCategory} from '../types.ts';

type FormValues = Application;

// FNP-1498: CREATING AN APPLICATION IS A NAME AND A DESCRIPTION.
//
// ═══ WHAT THIS FORM USED TO ASK ═══
//
// Eighteen fields, including repository names, a parent domain, a hosted zone,
// a certificate ARN and a deployment account — every one of them describing
// infrastructure that does not exist yet. Creating a record writes a row to
// DynamoDB: nothing is deployed, nothing is running, nothing is reachable. So
// nothing about accounts, zones, certificates or firewalls belongs here.
//
// It also asked for `email` and `phone`, which were collected and thrown away
// — neither was ever in the Application type or the update whitelist — and for
// BOTH `name` and `displayName`, which is the duplication that made an
// operator type the same thing twice.
//
// ⚠️ THE PROVISIONING FIELDS MOVED, THEY DID NOT VANISH. They belong to the
// act of provisioning, which is a separate verb with its own form (FNP-1500).
// Do not add one back here because it "feels like" a property of the record.
//
// ⚠️ `name` IS THE HUMAN-FRIENDLY NAME NOW. It used to double as the stem for
// derived repository names, which is why it carried a lowercase-and-hyphens
// rule and why it is immutable. The repository name is chosen at provisioning
// time instead, so this field is free to be an ordinary name.
export const FormComponent = () => {

    const {values} = useFormikContext<FormValues>();

    // The add drawer passes `{}`, the edit drawer passes a loaded record. No
    // explicit mode prop is needed and adding one would be a second source of
    // truth for the same fact.
    const isEdit = Boolean(values.id);

    return (
        <>
            <PlatformFieldset legend={'Application Details'}>

                {/*
                  * ⚠️ NOT EDITABLE ON THE EDIT FORM, and shown as text rather
                  * than a disabled input so nobody tries.
                  *
                  * `name` is immutable — console-api's update resolver throws
                  * NameImmutable on the field's mere PRESENCE. This form used
                  * to render it as a live input that `buildUpdateApplicationInput`
                  * silently dropped, so a rename returned a green "Application
                  * updated" toast and changed nothing. A refusal would have
                  * been better than that.
                  */}
                {isEdit ? (
                    <Field
                        name='name'
                        label='Name'
                        description='The application name cannot be changed after creation.'
                        component={FormikTextInput}
                        disabled
                        my={'xl'}
                    />
                ) : (
                    <Field
                        name='name'
                        label='Name'
                        description='The name people see. It cannot be changed later, so choose one you can live with.'
                        component={FormikTextInput}
                        withAsterisk
                        my={'xl'}
                    />
                )}

                <Field
                    name='description'
                    label='Description'
                    description='One line on what this application is for.'
                    component={FormikTextarea}
                    withAsterisk
                    my={'xl'}
                />

            </PlatformFieldset>

            {/*
              * Everything below is a property of an application that already
              * exists. On create it is absent entirely — the record is written
              * with sensible defaults and the operator fills these in when they
              * have something to say.
              */}
            {isEdit ? (
                <PlatformFieldset legend={'Application Properties'}>

                    <Field
                        name='applicationCategory'
                        label='Category'
                        description='Who this application is for. Admin applications are hidden from non-admin users.'
                        component={FormikSelect}
                        data={[
                            {
                                label: ApplicationCategory.ADMIN,
                                value: ApplicationCategory.ADMIN
                            },
                            {
                                label: ApplicationCategory.BUSINESS,
                                value: ApplicationCategory.BUSINESS
                            },
                            {
                                label: ApplicationCategory.CONSUMER,
                                value: ApplicationCategory.CONSUMER
                            },
                        ]}
                        withAsterisk
                        my={'xl'}
                    />

                    <Field
                        name='applicationStatus'
                        label='Status'
                        description='Please enter application status'
                        component={FormikRadioGroup}
                        data={[
                            {
                                label: 'Active',
                                value: 'ACTIVE'
                            },
                            {
                                label: 'Inactive',
                                value: 'INACTIVE'
                            },
                        ]}
                        withAsterisk
                        my={'xl'}
                    />

                    {/*
                      * ⚠️ WRITTEN BY PROVISIONING, editable here only to repair
                      * it. An application that was provisioned has its url set
                      * from the resolved stage address; typing one by hand does
                      * not move any DNS.
                      */}
                    <Field
                        name='url'
                        label='URL'
                        description='Where the application is reachable. Set automatically when the application is provisioned.'
                        component={FormikTextInput}
                        my={'xl'}
                    />

                    <Field
                        name='logoUrl'
                        label='Logo URL'
                        description='Please enter logo url'
                        component={FormikTextInput}
                        my={'xl'}
                    />

                    <Text size={'sm'} c={'dimmed'} mt={'xl'}>
                        Repositories, pipelines and DNS are set up by
                        Provision, not here.
                    </Text>

                </PlatformFieldset>
            ) : null}
        </>
    );
};
